import { Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LineItemsEditor } from "@/components/quotation/LineItemsEditor";
import { RentalFields } from "@/components/quotation/RentalFields";
import { RentalCalculator } from "@/components/quotation/RentalCalculator";

export const QUOTE_TYPES = [["sale", "Sale"], ["rental", "Rental, no buy option"], ["rental_buy", "Rental with buy option"]];
export const isRental = (t) => t === "rental" || t === "rental_buy";

// Swap boilerplate terms when the type changes, but keep anything the user customised.
export function retype(opt, type, d) {
  const toRental = isRental(type);
  const pick = (cur, saleDef, rentDef) => (!cur || cur === saleDef || cur === rentDef ? (toRental ? rentDef : saleDef) : cur);
  return {
    ...opt, quote_type: type,
    payment_terms: pick(opt.payment_terms, d.sale_payment_terms, d.payment_terms),
    warranty_terms: pick(opt.warranty_terms, d.sale_warranty_terms, d.warranty_terms),
    rental: toRental ? (opt.rental || { ...d.rental }) : opt.rental,
  };
}

const T = ({ children }) => <Label className="text-xs text-slate-500">{children}</Label>;

function Terms({ o, set, editable, p }) {
  const rental = isRental(o.quote_type);
  return (
    <div className="space-y-3">
      <div><T>{rental ? "Rental payment terms" : "Payment terms"}</T>
        <textarea value={o.payment_terms || ""} disabled={!editable} onChange={(e) => set({ payment_terms: e.target.value })} rows={2} className="mt-1 w-full border border-slate-200 rounded-md p-2 text-sm disabled:bg-slate-50" data-testid={`${p}payment-terms`} /></div>
      <div><T>Shipping terms</T><Input value={o.shipping_terms || ""} disabled={!editable} onChange={(e) => set({ shipping_terms: e.target.value })} className="mt-1" data-testid={`${p}shipping-terms`} /></div>
      <div><T>{rental ? "Warranty & AMC" : "Warranty terms"}</T><Input value={o.warranty_terms || ""} disabled={!editable} onChange={(e) => set({ warranty_terms: e.target.value })} className="mt-1" data-testid={`${p}warranty-terms`} /></div>
    </div>
  );
}

export function OptionCard({ o, letter, multi, onChange, onRemove, editable, catalog, defaults, isAdmin }) {
  const p = letter === "A" ? "" : `opt-${letter.toLowerCase()}-`;
  const set = (patch) => onChange({ ...o, ...patch });
  const rental = isRental(o.quote_type);
  return (
    <div className={`bg-white border rounded-lg p-4 space-y-4 ${multi ? "border-indigo-200" : "border-slate-200"}`} data-testid={`option-card-${letter}`}>
      <div className="flex flex-col sm:flex-row sm:items-end gap-3">
        {multi && <div className="text-sm font-bold text-indigo-700 shrink-0 sm:pb-2" data-testid={`option-letter-${letter}`}>Option {letter}</div>}
        <div className="sm:w-64">
          <T>Quote type</T>
          <Select value={o.quote_type || "sale"} onValueChange={(v) => onChange(retype(o, v, defaults))} disabled={!editable}>
            <SelectTrigger className="mt-1" data-testid={`${p}quote-type-select`}><SelectValue /></SelectTrigger>
            <SelectContent>{QUOTE_TYPES.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`${p}quote-type-${v}`}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {multi && <div className="flex-1"><T>Option name on PDF (optional)</T><Input value={o.label || ""} disabled={!editable} onChange={(e) => set({ label: e.target.value })} placeholder="e.g. Outright buy" className="mt-1" data-testid={`${p}option-label`} /></div>}
        <div className="flex gap-2 sm:ml-auto">
          {rental && isAdmin && editable && <RentalCalculator />}
          {multi && editable && letter !== "A" && <button type="button" onClick={onRemove} className="text-xs text-red-500 inline-flex items-center gap-1 px-2 py-2" data-testid={`${p}remove-option`}><Trash2 className="w-3.5 h-3.5" /> Remove option</button>}
        </div>
      </div>
      <LineItemsEditor items={o.line_items} onChange={(line_items) => set({ line_items })} catalog={catalog} editable={editable} rental={rental} testPrefix={p} />
      {rental && <RentalFields quoteType={o.quote_type} rental={o.rental} onChange={(r) => set({ rental: r })} editable={editable} testPrefix={p} />}
      <div className="border-t border-slate-100 pt-3">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Terms{multi ? ` · Option ${letter}` : ""}</div>
        <Terms o={o} set={set} editable={editable} p={p} />
      </div>
    </div>
  );
}
